export const GRADE_ORDER = ['A', 'B', 'C', 'D', 'F'];

export const GRADE_COLORS = {
    A: '#0E8A4F',
    B: '#3B9B45',
    C: '#B7791F',
    D: '#D9640A',
    F: '#C81E1E',
};

export const safeArray = (value) => (Array.isArray(value) ? value : []);

export const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
};

export const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

export const normalizeGrade = (grade) => {
    if (grade === null || grade === undefined || grade === '') return '-';
    return String(grade).trim().toUpperCase();
};

export const gradeToScore = (grade) => {
    const normalized = normalizeGrade(grade);
    if (normalized === '-') return 0;
    if (/^\d+(\.\d+)?$/.test(normalized)) {
        const numeric = Number(normalized);
        if (numeric <= 5) return Math.max(0, Math.min(100, Math.round((numeric / 5) * 100)));
        return Math.max(0, Math.min(100, Math.round(numeric)));
    }
    const map = { A: 100, B: 80, C: 60, D: 40, F: 20 };
    return map[normalized] ?? 0;
};

export const scoreToGrade = (score) => {
    const numeric = Number(score);
    if (Number.isNaN(numeric)) return '-';
    if (numeric >= 90) return 'A';
    if (numeric >= 75) return 'B';
    if (numeric >= 60) return 'C';
    if (numeric >= 40) return 'D';
    return 'F';
};

export const gradeTone = (grade) => {
    const normalized = normalizeGrade(grade);
    return GRADE_COLORS[normalized] || '#64748B';
};

export const vendorGradeFallback = (vendor) => {
    return normalizeGrade(vendor?.overall_grade || vendor?.current_assessment_grade || vendor?.last_assessment_grade || '-');
};

export const buildGradeBuckets = (vendors = []) => {
    const buckets = GRADE_ORDER.reduce((acc, grade) => {
        acc[grade] = 0;
        return acc;
    }, {});

    vendors.forEach((vendor) => {
        const grade = vendorGradeFallback(vendor);
        if (buckets[grade] !== undefined) {
            buckets[grade] += 1;
        }
    });

    return buckets;
};

export const pct = (value, max = 100) => {
    const numeric = Number(value) || 0;
    if (max <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((numeric / max) * 100)));
};

export const severityTone = (severity) => {
    switch (String(severity || '').toLowerCase()) {
        case 'critical': return { background: '#FDE8E8', color: '#C81E1E' };
        case 'high': return { background: '#FFF0E3', color: '#D9640A' };
        case 'medium': return { background: '#FFF4DD', color: '#B7791F' };
        case 'low': return { background: '#E7F7EE', color: '#0E8A4F' };
        default: return { background: '#E2E8F0', color: '#64748B' };
    }
};

// Business-criticality (Critical/High/Medium/Low) uses the exact same 4
// colors as severityTone() — same underlying hex values, just a differently
// named field. Aliased rather than duplicated.
export const criticalityTone = severityTone;

// Shared vendor create/edit form option lists — same field set/order as the
// old dashboard's AddVendor.js (left untouched there since it's still shared
// with still-dark pages). Used by both AddVendorModal.js (create) and
// VendorProfileModal.js (inline edit) so the two forms stay in sync via one
// definition instead of two manually-duplicated copies.
export const SERVICE_OPTIONS = [
    'Cloud Infrastructure (IaaS)', 'Cloud Platform (PaaS)', 'SaaS Application',
    'Collaboration & Communication', 'Payment Gateway', 'Development / Engineering Services',
    'Managed IT / MSP', 'Security / SOC / MSSP', 'Data Analytics / BI', 'HR / Payroll',
    'Finance / Accounting', 'Marketing / CRM', 'Logistics / Physical Services', 'Other',
].map((label) => ({ value: label, label }));

export const CATEGORY_OPTIONS = ['Auto', 'Low', 'Medium', 'High', 'Critical'].map((label) => ({ value: label, label }));

export const YES_NO_OPTIONS = [{ value: true, label: 'Yes' }, { value: false, label: 'No' }];

// Column-specific "nothing here yet" text, replacing the bare "-" that every
// empty cell used to show. A dash is the same glyph for facts that are not the
// same: a vendor with no scan history ("Never scanned") is a different state
// from one with no upcoming scan ("Not scheduled"), and both differ from a
// vendor that simply has no grade yet. Shared so the Vendors table and the
// drill-down modal — which render the same columns — can't drift apart.
export const MISSING_TEXT = {
    vendor: 'Unnamed vendor',
    service: 'Not specified',
    criticality: 'Not set',
    grade: 'No grade',
    nextScan: 'Not scheduled',
    lastScan: 'Never scanned',
    // Question bank
    question: 'No question text',
    category: 'Uncategorised',
    responseType: 'Not set',
    source: 'Unknown',
    // Scan findings / issues
    finding: 'No finding',
    issue: 'No description',
    severity: 'Unrated',
    mitre: 'Not mapped',
    count: 'None',
    // Assessments
    status: 'Unknown',
};
